import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Field, Icon, Stack, Text } from '@/design-system'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { STEP_NUMBER, TOTAL_STEPS, useOnboardingDraft, useStep, type VerifyChannel } from './progress'
import styles from './Onboarding.module.css'

const PREFIXES = [
  { code: '+1', label: 'US +1' },
  { code: '+52', label: 'MX +52' },
  { code: '+57', label: 'CO +57' },
  { code: '+55', label: 'BR +55' },
  { code: '+591', label: 'BO +591' },
]

const WHY = [
  { icon: 'key', title: 'Login codes', body: 'Instant, no carrier delays' },
  { icon: 'notifications_active', title: 'Payment alerts', body: 'Every card payment and transfer, if you want them' },
  { icon: 'forum', title: 'Support chat', body: 'Reply in the same thread, around the clock' },
]

/** Splits a stored "+52 55 1234 5678" into a known prefix and the rest. */
function splitPhone(phone: string) {
  const match = PREFIXES.find((p) => phone.replace(/\s/g, '').startsWith(p.code))
  return {
    prefix: match?.code ?? '+1',
    rest: match ? phone.replace(/\s/g, '').slice(match.code.length) : phone.replace(/[^\d]/g, ''),
  }
}

/** 06 · Step 2 of 5 — choose where the code goes. WhatsApp first, as designed. */
export function OnboardingVerify() {
  useStep('verify')
  const navigate = useNavigate()
  const [draft, update] = useOnboardingDraft()
  const initial = splitPhone(draft.phone)
  const [prefix, setPrefix] = useState(initial.prefix)
  const [number, setNumber] = useState(initial.rest)

  const valid = number.replace(/\D/g, '').length >= 7

  const go = (channel: VerifyChannel) => {
    update({ channel, phone: `${prefix} ${number.trim()}` })
    navigate('/onboarding/verify/code')
  }

  return (
    <FlowLayout
      step={STEP_NUMBER.verify}
      totalSteps={TOTAL_STEPS}
      back="/onboarding/create"
      footer={
        <>
          <Button size="lg" block iconStart="chat" disabled={!valid} onClick={() => go('whatsapp')}>
            Continue on WhatsApp
          </Button>
          <Stack direction="row" gap={2}>
            <Button variant="outline" size="md" block disabled={!valid} onClick={() => go('sms')}>
              SMS code
            </Button>
            <Button variant="outline" size="md" block onClick={() => go('email')}>
              Email code
            </Button>
          </Stack>
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text as="h1" variant="title" weight="bold">
            Verify on WhatsApp
          </Text>
          <Text variant="body" tone="secondary">
            Faster than SMS, and it works on patchy networks. We send the code to your WhatsApp.
          </Text>
        </Stack>

        <div className={styles.phone}>
          <select
            className={styles.prefix}
            value={prefix}
            onChange={(event) => setPrefix(event.target.value)}
            aria-label="Country code"
          >
            {PREFIXES.map((p) => (
              <option key={p.code} value={p.code}>
                {p.label}
              </option>
            ))}
          </select>
          <div style={{ flex: 1 }}>
            <Field
              label="WhatsApp number"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
          </div>
        </div>

        <Stack gap={2}>
          {WHY.map((item) => (
            <div key={item.title} className={styles.row}>
              <Icon name={item.icon} size={20} />
              <Stack gap={0}>
                <Text variant="caption" weight="semibold">
                  {item.title}
                </Text>
                <Text variant="caption" tone="secondary">
                  {item.body}
                </Text>
              </Stack>
            </div>
          ))}
        </Stack>

        <Text variant="caption" tone="secondary">
          We never share your number, and you can switch this off any time.
        </Text>
      </Stack>
    </FlowLayout>
  )
}
